/**
 * Common functionality shared between main and blog pages
 */
import './styles.css';

import emailjs from '@emailjs/browser';
import { initAnalytics } from './analytics';

// Initialize EmailJS right after import
emailjs.init({
    publicKey: process.env.EMAILJS_PUBLIC_KEY, // Replace with your actual public key
    blockHeadless: true,
});

// Create and animate background notes
export function setupBackgroundNotes() {
    console.log('🎵 setupBackgroundNotes called from common.js');

    const noteContainer = document.getElementById('noteContainer');
    const noteSymbols = ['♪', '♫', '♬', '♩', '♭', '♮', '♯'];

    console.log('🎵 noteContainer found:', !!noteContainer, noteContainer);

    if (!noteContainer) {
        console.error('🎵 Note container not found! Exiting setupBackgroundNotes');
        return;
    }

    console.log('🎵 Creating 40 background notes...');

    // Create initial notes
    for (let i = 0; i < 40; i++) {
        const note = document.createElement('div');
        note.classList.add("note");

        // Randomly select one of the note symbols
        const symbolIndex = Math.floor(Math.random() * noteSymbols.length);
        note.textContent = noteSymbols[symbolIndex];

        // Set random size (small, medium, large)
        const sizeClass = ['small', 'medium', 'large'][Math.floor(Math.random() * 3)];
        note.classList.add(sizeClass);

        // Random starting position
        const startPos = {
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight
        };

        note.style.left = startPos.x + 'px';
        note.style.top = startPos.y + 'px';

        noteContainer.appendChild(note);

        // Start animation
        startNoteAnimation(note);
    }

    console.log('🎵 Created', noteContainer.children.length, 'notes in container');

    // Schedule shooting stars
    scheduleShootingStars();

    // Create pulsing beat effect
    createBeatEffect();

    console.log('🎵 setupBackgroundNotes completed successfully');
}

// Function to start animation for a note
function startNoteAnimation(note) {
    animateNote(note);
}

// Function to animate notes
function animateNote(note) {
    // Calculate new position
    const endX = Math.random() * window.innerWidth;
    const endY = Math.random() * window.innerHeight;
    const duration = 20 + Math.random() * 10;
    const rotation = Math.random() * 360;

    // Set CSS transition
    note.style.transition = `transform ${duration}s linear, opacity 0.3s ease`;

    // Get current position
    const startX = parseFloat(note.style.left) || 0;
    const startY = parseFloat(note.style.top) || 0;

    // Calculate movement
    const dx = endX - startX;
    const dy = endY - startY;

    // Apply transform for animation
    requestAnimationFrame(() => {
        note.style.transform = `translate(${dx}px, ${dy}px) rotate(${rotation}deg)`;
    });

    // After animation completes, update position and start next animation
    setTimeout(() => {
        // Reset transition
        note.style.transition = 'none';

        // Update actual position
        note.style.left = endX + 'px';
        note.style.top = endY + 'px';

        // Reset transform
        note.style.transform = 'none';

        // Start next animation after a small delay
        setTimeout(() => {
            animateNote(note);
        }, 50);
    }, duration * 1000);
}

// Create shooting stars periodically
function scheduleShootingStars() {
    const noteContainer = document.getElementById('noteContainer');
    if (!noteContainer) return;

    const noteSymbols = ['♪', '♫', '♬', '♩', '♭', '♮', '♯'];

    function createShootingStar() {
        const shootingStar = document.createElement('div');
        shootingStar.className = 'shooting-star';

        // Randomly select a note symbol
        const noteSymbol = noteSymbols[Math.floor(Math.random() * noteSymbols.length)];
        shootingStar.textContent = noteSymbol;

        // Set random starting position along the top of the screen
        const startPositionX = Math.random() * 80 + 10; // 10-90% of screen width
        shootingStar.style.left = startPositionX + '%';

        // Set random color for the note
        const hue = Math.floor(Math.random() * 360);
        shootingStar.style.color = `hsl(${hue}, 100%, 75%)`;

        // Add to container
        noteContainer.appendChild(shootingStar);

        // Add animation keyframes style if not already added
        if (!document.getElementById('shooting-star-keyframes')) {
            const styleElement = document.createElement('style');
            styleElement.id = 'shooting-star-keyframes';
            styleElement.textContent = `
                @keyframes shootingStar {
                    0% { transform: translateY(0) translateX(0); opacity: 1; }
                    100% { transform: translateY(120vh) translateX(100px); opacity: 0; }
                }
            `;
            document.head.appendChild(styleElement);
        }

        // Animate
        shootingStar.style.animation = `shootingStar ${1 + Math.random() * 2}s ease-out forwards`;

        // Remove after animation completes
        setTimeout(() => {
            shootingStar.remove();
        }, 3000);
    }

    // Schedule next shooting star with random timing
    function scheduleNext() {
        const nextTime = 2000 + Math.random() * 4000;
        setTimeout(() => {
            createShootingStar();
            scheduleNext();
        }, nextTime);
    }

    scheduleNext();
}

// Create pulsing beat effect for notes
function createBeatEffect() {
    const notes = document.querySelectorAll('.note');
    if (!notes.length) return;

    const beatDuration = 60 / 50; // 50 BPM

    let beatIndex = 0;
    setInterval(() => {
        // Apply beat effect to a subset of notes (different group each beat)
        notes.forEach((note, index) => {
            if (index % 4 === beatIndex) {
                note.classList.add('beat');
                setTimeout(() => {
                    note.classList.remove('beat');
                }, beatDuration * 500); // Half beat duration
            }
        });

        beatIndex = (beatIndex + 1) % 4;
    }, beatDuration * 1000);
}

// Setup signup modal
export function setupSignupModal() {
    const modal = document.getElementById('signupModal');
    if (!modal) return;

    const ctaButtons = document.querySelectorAll('#cta-get-started, #cta-signup, #how-it-works-cta, #hero-signup, .producer-btn, .dj-btn');
    const closeBtn = document.querySelector('.modal-close');

    ctaButtons.forEach(btn => {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            modal.style.display = 'flex';

            // If the button has a role attribute, set the user type select
            const userTypeSelect = document.getElementById('userType');
            if (userTypeSelect) {
                if (this.classList.contains('producer-btn')) {
                    userTypeSelect.value = 'producer';
                } else if (this.classList.contains('dj-btn')) {
                    userTypeSelect.value = 'dj';
                }
            }
        });
    });

    if (closeBtn) {
        closeBtn.addEventListener('click', function() {
            modal.style.display = 'none';
        });
    }

    window.addEventListener('click', function(e) {
        if (e.target === modal) {
            modal.style.display = 'none';
        }
    });
    // Interest level radio buttons
    const interestRadios = document.querySelectorAll('input[name="interestLevel"]');
    const betaQuestions = document.getElementById('beta-questions');

    // Show/hide optional questions based on interest level
    interestRadios.forEach(radio => {
        radio.addEventListener('change', function() {
            if (this.value === 'beta') {
                betaQuestions.style.display = 'block';
            } else {
                betaQuestions.style.display = 'none';
            }
        });
    });

    // Update the form submission to include the new fields
    const signupForm = document.getElementById('signupForm');
    if (signupForm) {
        signupForm.addEventListener('submit', function(e) {
            e.preventDefault();

            // Validate user type
            // const userType = document.getElementById('userType');
            // if (userType.value === "") {
            //     alert('Please select whether you are a Music Producer or DJ/Live Performer');
            //     userType.focus();
            //     return;
            // }

            // Get reCAPTCHA response
            const recaptchaResponse = window.grecaptcha ? grecaptcha.getResponse() : '';

            // Validate reCAPTCHA
            if (window.grecaptcha && !recaptchaResponse) {
                alert('Please complete the CAPTCHA verification');
                return;
            }

            // Get selected interest level
            const interestLevel = document.querySelector('input[name="interestLevel"]:checked').value;

            // Interest level display names (for more readable emails)
            const interestLevelNames = {
                'updates': 'Keep me updated',
                'early-access': 'Early access',
                'beta': 'Beta tester'
            };

            // Get beta tester reason if applicable
            let betaReasonTextArea = document.getElementById('beta-reason')
            let betaReason = '';
            if (interestLevel === 'beta' && betaReasonTextArea) {
                betaReason = betaReasonTextArea.value;
                if (betaReason === "") {
                    alert('Please tell us why you\'d make a good beta user');
                    betaReasonTextArea.focus();
                    return;
                }
            }

            // Show loading state
            const submitButton = this.querySelector('button[type="submit"]');
            const originalButtonContent = submitButton.innerHTML;
            submitButton.innerHTML = '<span class="loading-spinner"></span> Processing...';
            submitButton.disabled = true;

            // Prepare form data for EmailJS
            const formData = {
                name: document.getElementById('name').value,
                email: document.getElementById('email').value,
                // userType: userType.value,
                userType: "producer",
                interestLevel: interestLevelNames[interestLevel] || interestLevel,
                betaReason: betaReason,
                signup_date: new Date().toLocaleString()
            };

            // Add reCAPTCHA response if available
            if (recaptchaResponse) {
                formData['g-recaptcha-response'] = recaptchaResponse;
            }

            // Send email using EmailJS
            emailjs.send(
                process.env.EMAILJS_SERVICE_ID,
                process.env.EMAILJS_TEMPLATE_ID,
                formData
            )
            // For demo purposes
            // (() => {
            //     return new Promise(function(resolve, reject) {
            //         setTimeout(() => {
            //             console.log("not sending real email")
            //             resolve("timeout completed")
            //         }, 300)
            //     });
            // })()
            .then(function(response) {
                console.log('SUCCESS!', response.status, response.text);

                // Get the form container
                const successMessage = document.createElement('div');
                successMessage.className = 'success-message';

                // Create different success messages based on interest level
                let successTitle, successText;

                switch(interestLevel) {
                    case 'beta':
                        successTitle = "You're on the Beta List!";
                        successText = "Thanks for signing up as a beta tester. We'll be in touch soon with more details.";
                        break;
                    case 'early-access':
                        successTitle = "Early Access Reserved!";
                        successText = "You're on the priority list for early access. We'll let you know when we're ready to launch.";
                        break;
                    default:
                        successTitle = "Thanks for Signing Up!";
                        successText = "We'll keep you updated on our progress and let you know when we launch.";
                }

                // Set the success message HTML
                successMessage.innerHTML = `
                    <div class="success-icon logo">
                        <img src="assets/temprom-logo.svg" alt="TEMPROM Logo">
                    </div>
                    <h3 class="success-title">${successTitle}</h3>
                    <p>${successText}</p>
                `;

                // Hide the form and show the success message
                signupForm.style.opacity = '0';
                setTimeout(() => {
                    let heading = document.querySelector('#join-waitlist-heading');
                    heading.textContent = 'Joined the Waitlist!'
                    signupForm.style.display = 'none';
                    signupForm.parentNode.appendChild(successMessage);

                    // Track the successful signup event if analytics is available
                    if (typeof gtag === 'function') {
                        gtag('event', 'signup', {
                            'event_category': 'Waitlist',
                            // 'event_label': userType.value
                            'event_label': 'producer'
                        });
                    }
                }, 300);
            })
            .catch(function(error) {
                console.error('FAILED...', error);
                alert('Oops! Something went wrong. Please try again later.');

                // Restore button state
                submitButton.innerHTML = originalButtonContent;
                submitButton.disabled = false;

                // Log error to console with details
                console.error('EmailJS Error Details:', error);

                // Track the error if analytics is available
                if (typeof gtag === 'function') {
                    gtag('event', 'signup_error', {
                        'event_category': 'Waitlist',
                        'event_label': error.text || 'Unknown error'
                    });
                }
            });
        });
    }
}

// Setup mobile menu
export function setupMobileMenu() {
    const mobileMenuBtn = document.querySelector('.btn-mobile-menu');
    const navMobile = document.querySelector('.mobile-nav');

    if (mobileMenuBtn && navMobile) {
        mobileMenuBtn.addEventListener('click', function() {
            navMobile.classList.toggle('active');
        });
    }
}

// Setup smooth scrolling for anchor links
export function setupSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        if (anchor.getAttribute('href') !== '#') {
            anchor.addEventListener('click', function(e) {
                e.preventDefault();
                const target = document.querySelector(this.getAttribute('href'));
                if (target) {
                    // Original behavior: manual fixed offset for header
                    window.scrollTo({
                        top: target.offsetTop - 80,
                        behavior: 'smooth'
                    });
                }
            });
        }
    });
}

export function initializeSite() {
    // Set up standard functionality
    setupBackgroundNotes();
    setupSignupModal();
    setupMobileMenu();
    setupSmoothScroll();

    // Initialize analytics
    initAnalytics();

    console.log('TEMPROM site fully initialized');
}